import gsap from 'gsap';
import { Container } from 'pixi.js';
import type { BookCore } from '@/modules/flipcore/BookCore';
import type {
  DisplayMode,
  ManualFlipEvent,
  ManualFlipRestoreEvent,
  ManualFlipStartEvent,
  PageChangeEvent
} from '@/types/book';
import type { ActionData } from '@/types';
import { ACTION_TYPE, CANVAS_MOBILE_BACKGROUND, CANVAS_PC_BACKGROUND } from '@/constants';
import { BookConstants } from '@/constants/bookConstants';
import { Canvas } from './Canvas.class';
import type { BookPage } from './book/BookPage.class';
import { FlipAction, PlayAction } from '../timeline/action';
import { Element, SoundElement } from './element';
import flipSoundUrl from '@/assets/music/flipsound.mp3';
import { isSpreadFlipTransition } from '@/modules/flipcore/display/pageTransition';
import { syncPlaybackToTime } from './tool/playbackSync';
import { BookZoomController } from './zoom/BookZoomController';
import { destroySharedFlipSoundElement, getSharedFlipSoundElement } from './tool/sharedFlipSound';
import { PageChangePlaybackGate } from './tool/PageChangePlaybackGate';
import { clientPointToElement } from '@/utils/readerViewportCoordinates';
import { PixiGlobal } from './PixiGlobal';

const CURRENT_PAGE_END_EPSILON = 0.001;

export class BookCanvas extends Canvas {
  private book: BookCore | null = null;

  /** 翻页 Action(翻页动画 + 音效合一)，按页序排列，仅 spread 切换对。 */
  private readonly flipActions: FlipAction[] = [];
  private flipActionsDisplayMode: DisplayMode | null = null;

  /**
   * 程序化同步进行中：抑制时间轴↔书本双向绑定的重入回声。
   * 时间轴驱动书本翻页、或手动翻页反向 seek 时间轴时置位，
   * 任一方向触发的回调看到它为 true 即跳过，避免互相回拨。
   */
  private isSyncing = false;

  private currentPage: BookPage | null = null;

  private zoomController: BookZoomController | null = null;
  private zoomStateHandler: ((active: boolean) => void) | null = null;

  /** 已按需 load 过内容的页 id，避免重复加载。 */
  private readonly loadedPageIds = new Set<string>();
  private readonly loadingPageTasks = new Map<string, Promise<void>>();

  private readonly pageChangePlaybackGate = new PageChangePlaybackGate();
  private pageChangeReadyHandler: ((pageIndex: number) => Promise<void>) | null = null;
  /** 时间轴驱动翻页时：当前跨页已预加载完成则可跳过 Gate，避免 pause/seek 顿音。 */
  private pageChangeReadyChecker: ((pageIndex: number) => boolean) | null = null;
  private pageChangeSettledHandler: ((pageIndex: number) => void) | null = null;
  /** Gate 等待期间收到的连续播放请求；优先级高于旧的“只播当前页”回调。 */
  private continuousPlaybackRequested = false;
  private manualFlipSession: {
    fromPageIndex: number;
    wasPlaying: boolean;
    targetPageIndex: number | null;
  } | null = null;

  /** 阅读端 BGM 淡出 tween（不挂主时间轴）。 */
  private bgmFadeTween: gsap.core.Tween | null = null;
  /** 阅读端 BGM 播放态回调（供 UI 同步）。 */
  private bgmPlaybackStateHandler: ((playing: boolean) => void) | null = null;
  /** 阅读端 BGM 就绪态回调（供 UI 控制按钮可用性）。 */
  private bgmReadyStateHandler: ((ready: boolean) => void) | null = null;
  private bgmPlaying = false;
  /** 自动播结束淡出后为 true，便于 resumeBgmAfterFade 恢复。 */
  private bgmFadedOut = false;

  constructor(container?: HTMLDivElement) {
    super(container);
    // 设置每页的翻页时长
    this.updatePagePaddingTime(BookConstants.FLIPPING_TIME / 1000);
  }

  /** 阅读端尺寸由 BookCanvas.vue 的 ResizeObserver 单独驱动。 */
  protected override shouldAutoResizeToContainer(): boolean {
    return false;
  }

  protected override initOthers() {
    // 保存编辑器的 PixiGlobal 引用，避免预览关闭后 showTextEditor 等依赖
    // 编辑器 Application 的功能报错。destroy 时通过 restore() 还原。
    PixiGlobal.save();
    if (this.app) PixiGlobal.init(this.app);
  }

  public override async init(container: HTMLDivElement) {
    await super.init(container);

    if (!this.app) return;

    const app = this.app;
    const events = app.renderer.events;
    events.mapPositionToPoint = (point, clientX, clientY) => {
      const mapped = clientPointToElement(
        events.domElement,
        clientX,
        clientY,
        app.screen.width,
        app.screen.height
      );
      point.x = mapped.x;
      point.y = mapped.y;
    };
  }

  /** 阅读端不注册第二套 renderer/window resize 写入者。 */
  protected override initEvents() {
    if (!this.stage) return;
    this.stage.eventMode = 'static';
  }

  /**
   * 同步 Pixi 画布背景色：
   * PC 端 #F8FAFC，移动端 #000000。
   * 在 init、resize 及视口切换时调用。
   * 需在 app.init 完成且 renderer 就绪后才可写背景，避免预览弹窗尺寸 watcher 抢跑。
   */
  public override syncCanvasBackground(): void {
    // 步骤 1：init 进行中或已 destroy 时 renderer/background 可能尚未就绪
    const background = this.app?.renderer?.background;
    if (!background) return;

    // 步骤 2：按当前视口写入背景色
    background.color = this.isMobileViewport() ? CANVAS_MOBILE_BACKGROUND : CANVAS_PC_BACKGROUND;
  }

  public async addBook(book: BookCore): Promise<void> {
    if (!this.app || !this.elementContainer) return;

    this.book = book;
    this.elementContainer.removeChildren();

    this.book.mount(this.elementContainer, this.app);
    this.book.ensurePageCount(this.pageMap.size);
    this.getOrderedPages().forEach((page, index) => {
      this.book?.setPageNeedSpineShadow(index, page.data?.needSpineShadow !== false);
    });
    this.book.addPageChangeListener(this.handleBookPageChange);
    this.book.onManualFlip = this.handleManualFlip;
    this.book.onManualFlipStart = this.handleManualFlipStart;
    this.book.onManualFlipRestore = this.handleManualFlipRestore;

    this.zoomController = new BookZoomController({
      book: this.book,
      app: this.app,
      stage: this.stage,
      onStateChange: (active) => this.zoomStateHandler?.(active)
    });

    await this.buildTimeline();
  }

  public removeBook() {
    this.cancelBgmFade();
    this.book?.removePageChangeListener(this.handleBookPageChange);
    if (this.book) {
      this.book.onManualFlip = null;
      this.book.onManualFlipStart = null;
      this.book.onManualFlipRestore = null;
    }
    this.clearFlipActions();
    this.clearTimeline();

    this.zoomController?.destroy();
    this.zoomController = null;
    this.zoomStateHandler = null;

    this.loadedPageIds.clear();
    this.loadingPageTasks.clear();
    this.clearPageChangeHandlers();

    this.book?.unmount();
    this.book = null;
  }

  /** 阅读端注入当前跨页的懒加载等待逻辑。 */
  public setPageChangeReadyHandler(handler: ((pageIndex: number) => Promise<void>) | null): void {
    this.pageChangeReadyHandler = handler;
  }

  /**
   * 阅读端注入「当前跨页是否已就绪」检查。
   * 时间轴自动翻页且已就绪时跳过 Gate，避免无谓 pause/seek。
   */
  public setPageChangeReadyChecker(checker: ((pageIndex: number) => boolean) | null): void {
    this.pageChangeReadyChecker = checker;
  }

  /** 仅在最后一次翻页完成加载并同步播放状态后通知外部。 */
  public setPageChangeSettledHandler(handler: ((pageIndex: number) => void) | null): void {
    this.pageChangeSettledHandler = handler;
  }

  private clearPageChangeHandlers(): void {
    this.pageChangePlaybackGate.cancel();
    this.continuousPlaybackRequested = false;
    this.manualFlipSession = null;
    this.getTimeline()?.clearPlaybackLimit();
    this.pageChangeReadyHandler = null;
    this.pageChangeReadyChecker = null;
    this.pageChangeSettledHandler = null;
  }

  /**
   * 确保给定索引集合对应的 pageMap 页已加载内容（音频/元素动画）。
   * 加载后重建该页 tween 并刷新可播放音频列表，供自动播放使用。
   * 时间轴的每页 startTime 由数据决定、保持不变。
   */
  public async ensurePagesLoadedByIndexes(indexes: number[]): Promise<void> {
    const pages = this.getOrderedPages();

    // 先卸载离开当前窗口的已加载页，释放其音频/动画资源，避免自动播放逐页累积
    // 导致移动端 WebView 内存/媒体资源超限被杀（表现为页面强制重载）。
    const unloaded = this.unloadPagesOutsideWindow(indexes, pages);

    const tasks: Promise<void>[] = [];

    for (const index of indexes) {
      const page = pages[index];
      if (!page || this.loadedPageIds.has(page.pageId)) continue;

      let task = this.loadingPageTasks.get(page.pageId);
      if (!task) {
        task = this.loadPageContent(page);
        this.loadingPageTasks.set(page.pageId, task);
      }
      tasks.push(task);
    }

    if (tasks.length === 0) {
      // 无新增加载但发生了卸载：同步刷新可播放 Action 列表，移除已卸载页的 PlayAction。
      if (unloaded) this.updatePlayActions();
      return;
    }

    await Promise.all(tasks);

    // 新加载页可能带音频 PlayAction，刷新供 play()/pause() 驱动。
    this.updatePlayActions();

    // 懒加载完成时若时间轴已在播放，补上新 Action 的 playing 状态；
    // 否则 PlayAction 只 seek 不 seekPlay，表现为该页无旁白（如第 3 个内容跨页）。
    if (this.getTimeline()?.playing) {
      this.playActions.forEach((action) => action.play());
    }
  }

  /**
   * 卸载不在给定窗口内的已加载页内容（保留页结构与主时间轴占位 tween，可再次懒加载）。
   * @returns 是否有页被卸载
   */
  private unloadPagesOutsideWindow(indexes: number[], pages: BookPage[]): boolean {
    if (this.loadedPageIds.size === 0) return false;

    const keepPageIds = new Set<string>();
    for (const index of indexes) {
      const page = pages[index];
      if (page) keepPageIds.add(page.pageId);
    }

    let unloaded = false;
    for (const page of pages) {
      if (!this.loadedPageIds.has(page.pageId) || keepPageIds.has(page.pageId)) continue;

      page.unload();
      this.loadedPageIds.delete(page.pageId);
      unloaded = true;
    }

    return unloaded;
  }

  private async loadPageContent(page: BookPage): Promise<void> {
    try {
      await page.load();
      page.initTween();
      this.loadedPageIds.add(page.pageId);
    } finally {
      this.loadingPageTasks.delete(page.pageId);
    }
  }

  /**
   * 按 DOM 已确认的最终尺寸同步 renderer 与舞台中心。
   * 阅读端旋转时用同一组尺寸完成两步更新，避免书本布局与舞台中心分别读取新旧视口。
   */
  public syncViewportSize(width: number, height: number): void {
    if (!this.app?.renderer || width <= 0 || height <= 0) return;

    const sizeChanged = this.app.screen.width !== width || this.app.screen.height !== height;
    if (sizeChanged) {
      this.zoomController?.prepareForViewportResize();
      this.app.renderer.resize(width, height);
    }

    // 阅读端没有额外的 renderer resize 监听，由当前快照同步舞台中心。
    this.resize();
  }

  /** renderer.resize 会清空后备缓冲；布局提交后立即补绘，避免等下一帧 ticker 时短暂闪白。 */
  public renderNow(): void {
    this.app?.render();
  }

  /** 把阅读端页面副本更新为最终显示像素尺寸，原始持久化宽高保持不变。 */
  public setPageDisplaySize(width: number, height: number): boolean {
    if (width <= 0 || height <= 0) return false;

    let changed = false;
    this.pageMap.forEach((page) => {
      if (page.data?.displayWidth === width && page.data?.displayHeight === height) return;
      page.bakeDisplaySize(width, height);
      changed = true;
    });
    return changed;
  }

  public destroy() {
    // 步骤 1：清阅读端 BGM 淡出与回调
    this.cancelBgmFade();
    this.clearBgmPlaybackEndedHandler();
    this.bgmPlaybackStateHandler = null;
    this.bgmReadyStateHandler = null;

    // 步骤 2：拆书本与翻页相关资源
    this.book?.removePageChangeListener(this.handleBookPageChange);
    this.clearFlipActions();
    this.clearPageChangeHandlers();
    destroySharedFlipSoundElement();

    this.zoomController?.destroy();
    this.zoomController = null;

    this.book?.destroy();

    // 步骤 3：基类销毁（含 bgmAction）
    super.destroy();

    // 步骤 4：还原 PixiGlobal 到编辑器 Application，
    // 确保关闭预览后 showTextEditor 等编辑器功能仍可正常使用。
    PixiGlobal.restore();
  }

  public override play(): void {
    if (this.pageChangePlaybackGate.deferPlay()) return;
    super.play();
  }

  public override pause(): void {
    this.continuousPlaybackRequested = false;
    this.pageChangePlaybackGate.notePause();
    super.pause();
  }

  public override seek(time: number): void {
    this.playActions.forEach((action) => action.stopForSeek());
    super.seek(time);
  }

  private pauseImmediately(): void {
    super.pause();
  }

  private playImmediately(): void {
    this.continuousPlaybackRequested = false;
    super.play();
  }

  private seekImmediately(time: number): void {
    this.runExclusive(() => this.seek(time));
  }

  /**
   * 重建主时间轴：临时摘掉 bgmAction 再调基类，避免 BGM 被挂进主时间轴。
   */
  public override initTimeline() {
    // 步骤 1：临时摘掉 bgmAction，避免基类插入时间轴
    const bgm = this.bgmAction;
    this.bgmAction = null;
    try {
      super.initTimeline();
    } finally {
      // 步骤 2：还原引用（不重新 addTween）
      this.bgmAction = bgm;
    }
  }

  /**
   * 收集可播放 Action：页内音效 + 翻页声，故意排除 BGM。
   */
  public override updatePlayActions() {
    this.playActions.clear();

    this.pageMap.forEach((page) => {
      page.actions.forEach((action) => {
        if (action instanceof PlayAction) this.playActions.set(action.id, action);
      });
    });

    this.flipActions.forEach((action) => this.playActions.set(action.id, action));
  }

  /**
   * 阅读端添加 BGM：只持有引用并开启循环，不插入主时间轴。
   */
  public override addBgm(action: PlayAction) {
    this.cancelBgmFade();
    if (this.bgmAction) {
      this.clearBgmPlaybackEndedHandler();
      this.bgmAction.destroy();
    }

    this.bgmAction = action;
    if (action.element instanceof SoundElement) {
      action.element.setLoop(true);
      action.element.setPlaybackEndedHandler(this.handleBgmPlaybackEnded);
    }
    this.bgmFadedOut = false;
    this.emitBgmPlaybackState(false);
    this.bgmReadyStateHandler?.(this.hasBgm());
  }

  /** 移除当前 BGM（不碰主时间轴）。 */
  public override removeBgm() {
    this.cancelBgmFade();
    if (this.bgmAction) {
      this.clearBgmPlaybackEndedHandler();
      this.bgmAction.destroy();
      this.bgmAction = null;
    }
    this.bgmFadedOut = false;
    this.emitBgmPlaybackState(false);
    this.bgmReadyStateHandler?.(false);
  }

  /** BGM 独立播放，不依赖主时间轴状态。 */
  public playBgm(restart = false, onBlocked?: () => void, onStarted?: () => void): boolean {
    this.cancelBgmFade();
    const sound = this.bgmAction?.element;
    if (!(sound instanceof SoundElement)) return false;

    sound.setPlaybackBlockedHandler(
      onBlocked
        ? () => {
            this.emitBgmPlaybackState(false);
            onBlocked();
          }
        : null
    );
    sound.setPlaybackStartedHandler(() => {
      this.emitBgmPlaybackState(true);
      onStarted?.();
    });

    sound.prepareForAutoplay();
    if (restart) sound.restart(0);
    else sound.resume();
    this.bgmFadedOut = false;
    this.emitBgmPlaybackState(true);
    return true;
  }

  /** 暂停 BGM 并取消淡出。 */
  public pauseBgm(): void {
    this.cancelBgmFade();
    if (this.bgmAction?.element instanceof SoundElement) this.bgmAction.element.pause();
    this.bgmFadedOut = false;
    this.emitBgmPlaybackState(false);
  }

  /** 是否存在可用的 BGM 音效元素。 */
  public hasBgm(): boolean {
    return this.bgmAction?.element instanceof SoundElement;
  }

  /** 注册 BGM 播放态变化回调。 */
  public setBgmPlaybackStateHandler(handler: ((playing: boolean) => void) | null): void {
    this.bgmPlaybackStateHandler = handler;
  }

  /** 注册 BGM 就绪态变化回调，并立即同步当前状态。 */
  public setBgmReadyStateHandler(handler: ((ready: boolean) => void) | null): void {
    this.bgmReadyStateHandler = handler;
    handler?.(this.hasBgm());
  }

  /** 取消进行中的 BGM 淡出并恢复增益。 */
  public cancelBgmFade(): void {
    this.bgmFadeTween?.kill();
    this.bgmFadeTween = null;
    if (this.bgmAction?.element instanceof SoundElement) {
      this.bgmAction.element.setPlaybackGain(1);
    }
  }

  /** 自动播结束时淡出 BGM。 */
  public fadeOutBgm(durationSeconds: number): void {
    this.cancelBgmFade();
    if (!this.bgmPlaying) return;
    const sound = this.bgmAction?.element;
    if (!(sound instanceof SoundElement)) return;

    sound.resume();
    this.emitBgmPlaybackState(true);
    const state = { gain: 1 };
    this.bgmFadeTween = gsap.to(state, {
      gain: 0,
      duration: Math.max(0, durationSeconds),
      ease: 'none',
      onUpdate: () => sound.setPlaybackGain(state.gain),
      onComplete: () => {
        sound.pause();
        this.bgmFadeTween = null;
        this.bgmFadedOut = true;
        this.emitBgmPlaybackState(false);
      }
    });
  }

  /** 仅恢复自动播放结束时淡出的 BGM，不覆盖用户主动暂停。 */
  public resumeBgmAfterFade(): void {
    if (this.bgmFadedOut) this.playBgm();
  }

  /** 同步内部播放标志并通知外部 handler。 */
  private emitBgmPlaybackState(playing: boolean): void {
    this.bgmPlaying = playing;
    this.bgmPlaybackStateHandler?.(playing);
  }

  private readonly handleBgmPlaybackEnded = () => {
    this.cancelBgmFade();
    this.bgmFadedOut = false;
    this.emitBgmPlaybackState(false);
  };

  private clearBgmPlaybackEndedHandler(): void {
    const sound = this.bgmAction?.element;
    if (sound instanceof SoundElement) sound.setPlaybackEndedHandler(null);
  }

  public getCanvas() {
    return this.app?.canvas ?? null;
  }

  public get container(): Container | null {
    return this.elementContainer;
  }

  /** 按页面总时长重建主时间轴，并构建翻页 Action(翻页动画 + 音效)。 */
  private async buildTimeline() {
    this.clearFlipActions();
    this.initTimeline();
    await this.buildFlipActions();
    this.updatePlayActions();
  }

  /**
   * 为每一对 spread 切换的相邻页构建翻页 Action(翻页动画 + 音效)，插入主时间轴。
   * 同一跨页内的相邻页不产生真实翻页，跳过。
   */
  private async buildFlipActions(refreshTimeline = true) {
    const flippingSeconds = BookConstants.FLIPPING_TIME / 1000;
    const pages = this.getOrderedPages();

    for (let i = 0; i < pages.length - 1; i++) {
      if (!this.isSpreadTransitionPair(i, i + 1)) continue;

      const startTime = this.getFlipStartTime(pages, i, flippingSeconds);
      const action = await this.createFlipAction(i, flippingSeconds);

      if (!action?.tween) continue;

      this.getTimeline()?.addTween(action.tween, startTime, true);
      this.flipActions.push(action);
    }

    this.flipActionsDisplayMode = this.book?.displayMode ?? null;
    if (refreshTimeline) this.getTimeline()?.forceSeek(0);
  }

  /** 创建单个翻页 Action(翻页动画 + 音效合一)。 */
  private async createFlipAction(
    index: number,
    flippingSeconds: number
  ): Promise<FlipAction | null> {
    const soundElement = await getSharedFlipSoundElement({
      content: flipSoundUrl,
      createElement: () => new SoundElement()
    });
    const playbackElement = soundElement ?? new Element();

    const actionData: ActionData = {
      id: `flip-${index}`,
      type: ACTION_TYPE.SOUND,
      startTime: 0,
      duration: soundElement?.mediaDuration || flippingSeconds
    };

    const action = new FlipAction(actionData, playbackElement as unknown as Element, {
      flipDuration: flippingSeconds,
      renderProgress: (progress) =>
        this.runExclusive(() => this.book?.flipBetweenPages(index, index + 1, progress, 'bottom'))
    });

    action.init();
    return action;
  }

  /** 判断相邻页切换是否应播放翻页音效。 */
  private isSpreadTransitionPair(fromPageIndex: number, toPageIndex: number): boolean {
    const displayMode = this.book?.displayMode ?? 'double';
    const pageCount = this.pageMap.size;
    return isSpreadFlipTransition(fromPageIndex, toPageIndex, displayMode, pageCount);
  }

  /** 计算相邻页翻页 motion / 音效的起始时间。 */
  private getFlipStartTime(pages: BookPage[], fromIndex: number, flippingSeconds: number): number {
    const toIndex = fromIndex + 1;
    const rawStart = (pages[toIndex].data?.startTime ?? 0) - flippingSeconds;
    const minStart = pages[fromIndex].data?.startTime ?? 0;
    return Math.max(rawStart, minStart);
  }

  /** 清理所有已构建的翻页 Action。 */
  private clearFlipActions() {
    const timeline = this.getTimeline();
    this.flipActions.forEach((action) => {
      if (action.tween) timeline?.removeTween(action.tween);
      action.destroy();
    });
    this.flipActions.length = 0;
    this.flipActionsDisplayMode = null;
  }

  /** 展示模式变化后，仅按新模式重建翻页轨道，并恢复原播放类型。 */
  public async syncFlipActionsForDisplayMode(): Promise<void> {
    const book = this.book;
    const timeline = this.getTimeline();
    if (!book || !timeline || this.flipActionsDisplayMode === book.displayMode) return;

    const currentPageIndex = book.currentPageIndex;
    const playbackOwner = timeline.owner;
    this.pauseImmediately();
    this.clearFlipActions();
    await this.buildFlipActions(false);
    this.updatePlayActions();

    this.runExclusive(() => {
      this.playActions.forEach((action) => action.stopForSeek());
      timeline.forceSeek(this.getPageStartTime(currentPageIndex));

      if (playbackOwner === 'main') {
        this.playImmediately();
      } else if (playbackOwner === 'page') {
        this.playVisiblePagesImmediately(currentPageIndex);
      }
    });
  }

  /**
   * 手动翻页时播放翻页音效（自动播放由时间轴上的 PlayAction 驱动）。
   */
  private async playManualFlipSound() {
    // 主静音时不播放翻页声（自动播放音效由主增益置 0，已天然静音）。
    if (this.masterMuted) return;

    const flipSound = await getSharedFlipSoundElement({
      content: flipSoundUrl,
      createElement: () => new SoundElement()
    });
    if (!flipSound) return;

    // 重头播放：快速翻页时打断前一次声音，避免叠音或丢声。
    flipSound.restart(0);
  }

  /**
   * 进入同步窗口执行 fn；若已在同步中则跳过，
   * 借此打断时间轴↔书本双向绑定的重入回声。
   */
  private runExclusive(fn: () => void) {
    if (this.isSyncing) return;
    this.isSyncing = true;
    try {
      fn();
    } finally {
      this.isSyncing = false;
    }
  }

  /** 按页面索引返回稳定顺序，确保翻页 motion 与主时间轴使用同一页序。 */
  private getOrderedPages(): BookPage[] {
    return Array.from(this.pageMap.values()).sort((a, b) => a.pageIndex - b.pageIndex);
  }

  /** 阅读端加载管理器：按索引取 pageMap 唯一页。 */
  public getPageByIndex(index: number): BookPage | null {
    const pages = this.getOrderedPages();
    return pages.find((item) => item.pageIndex === index) ?? pages[index] ?? null;
  }

  /** 阅读端加载管理器：pageMap 页数量。 */
  public getPageCount(): number {
    return this.pageMap.size;
  }

  /** 页内容就绪后刷新 PlayAction；若时间轴在播则补上 playing。 */
  public onPageContentReady(): void {
    this.updatePlayActions();
    if (this.getTimeline()?.playing) {
      this.playActions.forEach((action) => action.play());
    }
  }

  /** 页内容卸载后刷新 PlayAction，去掉已释放旁白。 */
  public onPageContentUnloaded(): void {
    this.updatePlayActions();
  }

  /**
   * 解析播放起始页索引。
   * 双页封面跨页左侧为 -1 时，取右侧可见页作为起始页。
   */
  private resolvePlaybackPageIndex(pageIndex: number): number {
    if (pageIndex < 0 && this.book?.displayMode === 'double') {
      return pageIndex + 1;
    }

    return pageIndex;
  }

  /**
   * 获取指定页在时间轴上的起始时间。
   * 双页模式下 pageIndex 为跨页左侧页索引。
   */
  public getPageStartTime(pageIndex: number): number {
    const resolvedIndex = this.resolvePlaybackPageIndex(pageIndex);
    const pages = this.getOrderedPages();
    const page = pages.find((item) => item.pageIndex === resolvedIndex) ?? pages[resolvedIndex];

    return page?.data?.startTime ?? 0;
  }

  /** 当前可见单页/跨页的结束时刻，不包含下一次翻页 Action。 */
  private getVisiblePagesEndTime(pageIndex: number): number {
    const resolvedIndex = this.resolvePlaybackPageIndex(pageIndex);
    const visiblePageIndexes =
      this.book?.displayMode === 'double' && pageIndex >= 0
        ? [resolvedIndex, resolvedIndex + 1]
        : [resolvedIndex];
    const visiblePages = visiblePageIndexes
      .map((index) => this.getPageByIndex(index))
      .filter((page): page is BookPage => page !== null);
    const lastVisiblePage = visiblePages.at(-1);

    return lastVisiblePage
      ? (lastVisiblePage.data?.startTime ?? 0) + lastVisiblePage.duration
      : this.getPageStartTime(pageIndex);
  }

  /** 已完成 seek 后，只播放当前可见页，到边界前暂停且不进入下一次翻页。 */
  private playVisiblePagesImmediately(pageIndex: number): void {
    const timeline = this.getTimeline();
    if (!timeline) return;

    this.continuousPlaybackRequested = false;
    const startTime = this.getPageStartTime(pageIndex);
    const endTime = this.getVisiblePagesEndTime(pageIndex);
    const playbackLimit = endTime - CURRENT_PAGE_END_EPSILON;

    timeline.clearPlaybackLimit();
    if (playbackLimit <= startTime) {
      this.pauseImmediately();
      return;
    }

    this.playActions.forEach((action) => action.play());
    timeline.playUntil(playbackLimit, () => this.pauseImmediately());
  }

  /** 从页首重新播放当前可见单页/跨页。 */
  private playVisiblePagesFromStart(pageIndex: number): void {
    const startTime = this.getPageStartTime(pageIndex);

    this.runExclusive(() => {
      this.pauseImmediately();
      this.seek(startTime);
      this.playVisiblePagesImmediately(pageIndex);
    });
  }

  /** 从指定页的起始时刻开始播放。 */
  public playFromPageIndex(pageIndex: number): void {
    this.pause();
    this.manualFlipSession = null;
    this.continuousPlaybackRequested = true;
    this.getTimeline()?.clearPlaybackLimit();
    if (this.book?.isAnimating()) return;

    this.seek(this.getPageStartTime(pageIndex));
    this.play();
  }

  /** 参考发布阅读器逻辑，在真实手势内先预热启动页会使用的音频元素。 */
  public prepareStartupAudioForUserGesture(): void {
    const sounds = new Set<SoundElement>();
    const bgm = this.bgmAction?.element;
    if (bgm instanceof SoundElement) sounds.add(bgm);

    const cover = this.getPageByIndex(0);
    cover?.actions.forEach((action) => {
      if (action instanceof PlayAction && action.element instanceof SoundElement) {
        sounds.add(action.element);
      }
    });

    sounds.forEach((sound) => sound.prepareForUserGesture());
  }

  /** 初次进入只播放封面音频与 BGM，不启动主时间轴。 */
  public playStartupAudio(
    onBlocked: () => void,
    onCompleted: () => void,
    onStarted?: () => void
  ): boolean {
    this.cancelBgmFade();

    const cover = this.getPageByIndex(0);
    const actions = cover
      ? Array.from(cover.actions.values()).filter(
          (action): action is PlayAction => action instanceof PlayAction
        )
      : [];

    let attempted = this.playBgm(true, onBlocked, onStarted);
    for (const action of new Set(actions)) {
      const sound = action.element;
      if (!(sound instanceof SoundElement)) continue;

      attempted = true;
      sound.setPlaybackBlockedHandler(onBlocked);
      sound.setPlaybackStartedHandler(onStarted ?? null);
      sound.setPlaybackEndedHandler(onCompleted);
      sound.prepareForAutoplay();
      sound.restart(0);
    }

    return attempted;
  }

  /** 记录真实拖拽开始时的原页播放状态，并立即暂停页面时间线。 */
  private readonly handleManualFlipStart = (event: ManualFlipStartEvent) => {
    this.beginManualFlipSession(event.pageIndex);
  };

  private beginManualFlipSession(fromPageIndex: number): void {
    if (this.manualFlipSession?.fromPageIndex === fromPageIndex) return;

    const timeline = this.getTimeline();
    const wasPlaying = timeline?.playing ?? false;
    timeline?.clearPlaybackLimit();
    this.pause();

    this.manualFlipSession = {
      fromPageIndex,
      wasPlaying,
      targetPageIndex: null
    };
  }

  /**
   * 人工翻页「翻过去」时在动画起点触发即时翻页音效，并记录目标页；
   * 程序翻页只允许主时间线驱动音频。
   */
  private readonly handleManualFlip = (event: ManualFlipEvent) => {
    if (event.source !== 'user' || this.continuousPlaybackRequested) return;

    if (this.isSpreadTransitionPair(event.fromPageIndex, event.toPageIndex)) {
      void this.playManualFlipSound();
    }

    this.beginManualFlipSession(event.fromPageIndex);
    if (this.manualFlipSession) {
      this.manualFlipSession.targetPageIndex = event.toPageIndex;
    }
  };

  /** 人工拖拽回弹结束：仅原页拖拽前仍在播放时，从原页页首重新播放当前可见页。 */
  private readonly handleManualFlipRestore = (event: ManualFlipRestoreEvent) => {
    const session = this.manualFlipSession;
    if (!session || session.fromPageIndex !== event.pageIndex) return;

    this.manualFlipSession = null;
    if (session.wasPlaying && this.book?.currentPageIndex === event.pageIndex) {
      this.playVisiblePagesFromStart(event.pageIndex);
    }
  };

  /**
   * 页码变化（手动翻页完成、tick 恢复或自动播放）后，
   * 必要时等待当前跨页就绪并 seek 页首。
   *
   * 时间轴驱动翻页时 onPageChange 仍在 runExclusive（isSyncing）内：
   * 必须延后到 sync 结束后再处理，否则 seekImmediately 会被 isSyncing 短路。
   */
  private readonly handleBookPageChange = (event: PageChangeEvent) => {
    const timelineDriven = this.isSyncing;
    const shouldPlayVisiblePages = this.manualFlipSession?.targetPageIndex === event.pageIndex;
    if (shouldPlayVisiblePages) this.manualFlipSession = null;

    // onPageChange 返回后渲染器才会启动排队的下一次翻页；延迟判断可跳过中间页。
    queueMicrotask(() => {
      if (!this.book || this.book.currentPageIndex !== event.pageIndex || this.book.isAnimating()) {
        return;
      }

      this.syncPlaybackAfterPageChange(event, timelineDriven, shouldPlayVisiblePages);
    });
  };

  /**
   * 翻页后对齐时间轴。
   * @param timelineDriven 为 true 时：当前跨页已预加载则跳过 Gate，避免顿音。
   */
  private syncPlaybackAfterPageChange(
    event: PageChangeEvent,
    timelineDriven: boolean,
    playVisiblePages: boolean
  ): void {
    // 自动播且当前跨页已就绪：后方预加载不挡播，时间轴连续走
    if (timelineDriven && this.pageChangeReadyChecker?.(event.pageIndex)) {
      this.pageChangeSettledHandler?.(event.pageIndex);
      return;
    }

    const startTime = this.getPageStartTime(event.pageIndex);

    if (!this.pageChangeReadyHandler) {
      const shouldPlay =
        this.continuousPlaybackRequested ||
        playVisiblePages ||
        (this.getTimeline()?.playing ?? false);

      this.runExclusive(() => {
        syncPlaybackToTime(startTime, shouldPlay, {
          pause: () => this.pauseImmediately(),
          seek: (time) => this.seek(time),
          play: () => this.resumePlaybackAfterPageChange(event.pageIndex, playVisiblePages)
        });
      });
      return;
    }

    if (playVisiblePages) this.pageChangePlaybackGate.requestPlay();

    void this.pageChangePlaybackGate
      .waitAndSync(
        startTime,
        () => this.pageChangeReadyHandler?.(event.pageIndex) ?? Promise.resolve(),
        () => this.book?.currentPageIndex === event.pageIndex && !this.book.isAnimating(),
        {
          isPlaying: () =>
            this.continuousPlaybackRequested ||
            playVisiblePages ||
            (this.getTimeline()?.playing ?? false),
          pause: () => this.pauseImmediately(),
          seek: (time) => this.seekImmediately(time),
          play: () => this.resumePlaybackAfterPageChange(event.pageIndex, playVisiblePages)
        }
      )
      .then((settled) => {
        if (settled) this.pageChangeSettledHandler?.(event.pageIndex);
      })
      .catch((error) => {
        console.error('等待翻页资源加载失败:', error);
      });
  }

  /** Gate 完成时采用最后一次播放意图，连续播放可覆盖旧的当前页有界播放。 */
  private resumePlaybackAfterPageChange(pageIndex: number, playVisiblePages: boolean): void {
    const shouldPlayVisiblePages = playVisiblePages && !this.continuousPlaybackRequested;
    this.continuousPlaybackRequested = false;

    if (shouldPlayVisiblePages) {
      this.playVisiblePagesImmediately(pageIndex);
      return;
    }

    this.playImmediately();
  }

  // ==================== 页焦点管理 ====================

  public setCurrentPage(id: string) {
    if (this.currentPage?.pageId === id) return;

    if (this.currentPage) {
      if (this.currentPage.parent) this.currentPage.parent.removeChild(this.currentPage);
      this.currentPage.filters = null;
    }

    this.currentPage = this.getPage(id);
  }

  public getCurrentPage(): BookPage | null {
    return this.currentPage;
  }

  public isZooming(): boolean {
    return this.zoomController?.isActive() ?? false;
  }

  /** 注册缩放模式状态变化回调，供底部控制条同步放大/恢复按钮。 */
  public setOnZoomStateChange(handler: ((active: boolean) => void) | null): void {
    this.zoomStateHandler = handler;
  }

  /** 切换书本缩放模式。 */
  public toggleZoom(): void {
    this.zoomController?.toggle();
  }
}
